import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEvent, createEvent, updateEvent, getTeamMembers, getContacts } from '../utils/api';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC',
  'ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const EMPTY = {
  name: '', start_date: '', end_date: '', city: '', state: '', venue: '', notes: '',
  attendee_ids: [], contact_ids: [],
};

export default function EventFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(EMPTY);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      getTeamMembers().then(r => setTeamMembers(r.data || r)),
      getContacts({ per_page: 200 }).then(r => setAllContacts(r.data || [])),
      isEdit ? getEvent(id).then(ev => setForm({
        name: ev.name, start_date: ev.start_date, end_date: ev.end_date,
        city: ev.city || '', state: ev.state || '', venue: ev.venue || '', notes: ev.notes || '',
        attendee_ids: (ev.attendees || []).map(a => a.id),
        contact_ids: (ev.contacts || []).map(c => c.id),
      })) : Promise.resolve(),
    ])
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const toggleAttendee = (memberId) => {
    setForm(f => ({
      ...f,
      attendee_ids: f.attendee_ids.includes(memberId)
        ? f.attendee_ids.filter(x => x !== memberId)
        : [...f.attendee_ids, memberId],
    }));
  };

  const toggleContact = (contactId) => {
    setForm(f => ({
      ...f,
      contact_ids: f.contact_ids.includes(contactId)
        ? f.contact_ids.filter(x => x !== contactId)
        : [...f.contact_ids, contactId],
    }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      setError('Name, start date, and end date are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateEvent(id, form);
        navigate(`/events/${id}`);
      } else {
        const created = await createEvent(form);
        navigate(`/events/${created.id}`);
      }
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const filteredContacts = allContacts.filter(c => {
    if (!contactSearch) return true;
    const q = contactSearch.toLowerCase();
    return `${c.first_name} ${c.last_name} ${c.organization || ''}`.toLowerCase().includes(q);
  });

  if (loading) return <div className="page-loading">Loading…</div>;

  return (
    <div className="page-container" style={{ maxWidth: 700 }}>
      <div className="breadcrumb">
        <span className="breadcrumb-link" onClick={() => navigate('/events')}>Events</span>
        <span>/</span>
        <span>{isEdit ? 'Edit Event' : 'New Event'}</span>
      </div>

      <h1 className="page-title">{isEdit ? 'Edit Event' : 'New Event'}</h1>

      {error && <div className="form-error form-error-banner">{error}</div>}

      <div className="form-card">
        {/* Core fields */}
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Event Name *</label>
            <input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. ICSC Las Vegas 2026" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start Date *</label>
            <input type="date" className="input" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">End Date *</label>
            <input type="date" className="input" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Venue</label>
            <input className="input" value={form.venue} onChange={e => set('venue', e.target.value)} placeholder="e.g. Las Vegas Convention Center" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">City</label>
            <input className="input" value={form.city} onChange={e => set('city', e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: '0 0 120px' }}>
            <label className="form-label">State</label>
            <select className="input" value={form.state} onChange={e => set('state', e.target.value)}>
              <option value="">—</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Attendees */}
        <div className="form-group">
          <label className="form-label">Team Attending</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {teamMembers.map(m => (
              <button
                key={m.id}
                type="button"
                className={`toggle-pill ${form.attendee_ids.includes(m.id) ? 'active' : ''}`}
                onClick={() => toggleAttendee(m.id)}
              >
                {m.display_name}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="form-group">
          <label className="form-label">Notes / Agenda</label>
          <textarea className="input" rows={4} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Session notes, agenda, goals for the event…" />
        </div>

        {/* Contact linking */}
        <div className="form-group">
          <label className="form-label">
            Linked Contacts
            {form.contact_ids.length > 0 && (
              <span className="badge badge-neutral" style={{ marginLeft: 8 }}>{form.contact_ids.length} selected</span>
            )}
          </label>
          <input
            className="input"
            placeholder="Search contacts…"
            value={contactSearch}
            onChange={e => setContactSearch(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6 }}>
            {filteredContacts.map((c, i) => (
              <label key={c.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                cursor: 'pointer', borderBottom: i < filteredContacts.length - 1 ? '1px solid var(--color-border-faint)' : 'none',
                background: form.contact_ids.includes(c.id) ? 'var(--color-hover)' : 'transparent',
              }}>
                <input
                  type="checkbox"
                  checked={form.contact_ids.includes(c.id)}
                  onChange={() => toggleContact(c.id)}
                  style={{ accentColor: 'var(--color-success)' }}
                />
                <div className={`contact-avatar contact-avatar-sm avatar-${c.category}`}>
                  {c.first_name?.[0]}{c.last_name?.[0]}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-text-primary)' }}>
                    {c.first_name} {c.last_name}
                  </div>
                  {c.organization && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>{c.organization}</div>
                  )}
                </div>
              </label>
            ))}
            {filteredContacts.length === 0 && (
              <div style={{ padding: 12, color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>No contacts found</div>
            )}
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={() => navigate(isEdit ? `/events/${id}` : '/events')} disabled={saving}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Event'}
        </button>
      </div>
    </div>
  );
}
